import React, { forwardRef, useState } from 'react'
import { clsx } from 'clsx'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  helperText?: string
  error?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  variant?: 'default' | 'filled' | 'outline'
  fullWidth?: boolean
  isPassword?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      label,
      helperText,
      error,
      leftIcon,
      rightIcon,
      variant = 'default',
      fullWidth = true,
      isPassword = false,
      disabled,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    
    const actualType = isPassword ? (showPassword ? 'text' : 'password') : type

    const baseStyles = clsx(
      'flex h-10 rounded-md border transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
      fullWidth && 'w-full'
    )

    const variantStyles = {
      default: clsx(
        'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2',
        'focus:border-primary-500 focus:ring-primary-500',
        error && 'border-red-500 focus:border-red-500 focus:ring-red-500'
      ),
      filled: clsx(
        'border-transparent bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2',
        'focus:bg-white dark:focus:bg-gray-800 focus:border-primary-500 focus:ring-primary-500',
        error && 'bg-red-50 dark:bg-red-900/10 focus:border-red-500 focus:ring-red-500'
      ),
      outline: clsx(
        'border-2 border-gray-300 dark:border-gray-600 bg-transparent text-gray-900 dark:text-white px-3 py-2',
        'focus:border-primary-500 focus:ring-0',
        error && 'border-red-500 focus:border-red-500'
      ),
    }[variant]

    const iconColor = isFocused ? 'text-primary-500' : 'text-gray-400'

    return (
      <div className={fullWidth ? 'w-full' : 'inline-block'}>
        {label && (
          <label className={clsx(
            'block text-sm font-normal mb-2',
            error ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300',
            disabled && 'text-gray-400'
          )}>
            {label}
          </label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <div className={clsx(
              'absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none',
              iconColor
            )}>
              {leftIcon}
            </div>
          )}
          
          <input
            type={actualType}
            className={clsx(
              baseStyles,
              variantStyles,
              leftIcon && 'pl-10',
              (rightIcon || isPassword) && 'pr-10',
              className
            )}
            ref={ref}
            disabled={disabled}
            onFocus={(e) => {
              setIsFocused(true)
              props.onFocus?.(e)
            }}
            onBlur={(e) => {
              setIsFocused(false)
              props.onBlur?.(e)
            }}
            {...props}
          />
          
          {(rightIcon || isPassword) && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {isPassword ? (
                <button
                  type="button"
                  className={clsx('h-5 w-5 cursor-pointer', iconColor)}
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={disabled}
                >
                  {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </button>
              ) : rightIcon ? (
                <div className={clsx('h-5 w-5', iconColor)}>
                  {rightIcon}
                </div>
              ) : null}
            </div>
          )}
        </div>
        
        {(error || helperText) && (
          <div className={clsx(
            'mt-2 text-xs',
            error ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'
          )}>
            {error || helperText}
          </div>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export { Input }